import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fundingAuthAPI } from '@/utils/api';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { useTemuPageShopScope } from '@/temu-platform/hooks/useTemuPageShopScope';
import { TEMU_NOTICE_FAQ_ITEMS } from '@/temu-platform/data/temuNoticeFaq';
import { resolveTemuAlertTone } from '@/temu-platform/utils/alertTone';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';
import '@/styles/temu-platform/temu-notice-help.css';

const TABS = [
  { id: 'notices', label: '全部通知' },
  { id: 'faq', label: '常见问题' },
  { id: 'feedback', label: '问题反馈' },
];

const CHANNEL_FILTERS = [
  { id: 'all', label: '全部' },
  { id: 'platform', label: '平台通知' },
  { id: 'system', label: '系统通知' },
];

function sourceLabel(source) {
  if (source === 'webhook') return fixedUi('平台推送');
  if (source === 'sync') return fixedUi('系统同步');
  if (source === 'business') return fixedUi('业务待办');
  return '—';
}

function channelLabel(channel) {
  return channel === 'system' ? fixedUi('系统通知') : fixedUi('平台通知');
}

function levelLabel(tone) {
  if (tone === 'error') return fixedUi('异常');
  if (tone === 'warning') return fixedUi('警告');
  return fixedUi('正常');
}

function formatTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function matchKeyword(text, keyword) {
  if (!keyword) return true;
  return String(text || '').toLowerCase().includes(keyword);
}

export default function TemuNoticeHelpPage() {
  const {
    activeShop,
    shopDimensionTabs,
    concreteScopeId,
  } = useTemuPageShopScope();
  const activeShopId = concreteScopeId;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'notices';
  const channelFromUrl = searchParams.get('channel') || 'all';

  const [tab, setTab] = useState(
    TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : 'notices',
  );
  const [channelFilter, setChannelFilter] = useState(
    channelFromUrl === 'system' || channelFromUrl === 'platform' ? channelFromUrl : 'all',
  );
  const [keywordInput, setKeywordInput] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');

  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  useEffect(() => {
    const nextTab = TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : 'notices';
    setTab(nextTab);
  }, [tabFromUrl]);

  useEffect(() => {
    if (channelFromUrl === 'system' || channelFromUrl === 'platform') {
      setChannelFilter(channelFromUrl);
    }
  }, [channelFromUrl]);

  const switchTab = (next) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const loadAlerts = useCallback(async () => {
    if (!activeShopId) {
      setAlerts([]);
      return;
    }
    setLoading(true);
    try {
      const res = await temuPlatformAPI.getAlerts(activeShopId);
      setAlerts(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      setAlerts([]);
      showToast(e?.message || '加载通知失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeShopId]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const normalizedKeyword = appliedKeyword.trim().toLowerCase();

  const filteredAlerts = useMemo(() => {
    return alerts.filter((item) => {
      if (!item?.id) return false;
      const ch = item.channel || 'platform';
      if (channelFilter !== 'all' && ch !== channelFilter) return false;
      if (!normalizedKeyword) return true;
      const hay = [
        item.title,
        item.message,
        channelLabel(ch),
        sourceLabel(item.source),
      ].join(' ');
      return matchKeyword(hay, normalizedKeyword);
    });
  }, [alerts, channelFilter, normalizedKeyword]);

  const filteredFaq = useMemo(() => {
    return TEMU_NOTICE_FAQ_ITEMS.filter((item) => {
      if (!normalizedKeyword) return true;
      const hay = [
        item.title,
        item.answer,
        item.category,
        fixedUi(item.title),
        fixedUi(item.answer),
        fixedUi(item.category),
        ...(Array.isArray(item.keywords) ? item.keywords : []),
      ].join(' ');
      return matchKeyword(hay, normalizedKeyword);
    });
  }, [normalizedKeyword]);

  const handleSearch = (event) => {
    event?.preventDefault?.();
    setAppliedKeyword(keywordInput.trim());
  };

  const handleResetSearch = () => {
    setKeywordInput('');
    setAppliedKeyword('');
  };

  const handleDismiss = async (item) => {
    if (!activeShopId || !item?.id || busyId) return;
    setBusyId(item.id);
    try {
      await temuPlatformAPI.dismissAlert({
        shopId: activeShopId,
        channel: item.channel || 'platform',
        alertId: item.id,
        fingerprint: item.fingerprint || undefined,
      });
      setAlerts((prev) => prev.filter((row) => row.id !== item.id));
      showToast(fixedUi("已清除该通知"), 'success');
    } catch (e) {
      showToast(e?.message || '清除失败', 'error');
    } finally {
      setBusyId('');
    }
  };

  const submitFeedback = async (event) => {
    event?.preventDefault?.();
    const email = feedbackEmail.trim();
    const text = feedbackText.trim();
    if (!email) {
      showToast(fixedUi("请填写联系邮箱"), 'warning');
      return;
    }
    if (!text) {
      showToast(fixedUi("请填写问题描述"), 'warning');
      return;
    }
    if (text.length < 5) {
      showToast(fixedUi("问题描述至少 5 个字"), 'warning');
      return;
    }
    setFeedbackSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append(
        'content',
        [
          text,
          '',
          '【来源】TEMU 通知帮助中心',
          activeShop?.shopName ? `【店铺】${activeShop.shopName}` : '',
          activeShopId ? `【店铺ID】${activeShopId}` : '',
        ].filter(Boolean).join('\n'),
      );
      if (typeof window !== 'undefined') {
        formData.append('pageUrl', window.location.href);
      }
      await fundingAuthAPI.submitFeedback(formData);
      showToast(fixedUi("反馈已发送至系统邮箱"), 'success');
      setFeedbackText('');
    } catch (e) {
      showToast(e?.message || '提交失败', 'error');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const emptyMessage = !activeShopId
    ? fixedUi("请先选择已授权的 TEMU 店铺")
    : loading
      ? fixedUi("加载通知…")
      : (normalizedKeyword ? '没有匹配的通知' : '暂无通知');

  return (
    <div className="tpo-help-page" data-testid="temu-notice-help-page">
      <header className="tpo-help-region tpo-help-region--header">
        <div className="tpo-help-region__main">
          <h1 className="tpo-help-page__title">{fixedUi("通知与帮助")}</h1>
        </div>
        <form className="tpo-help-page__search" onSubmit={handleSearch}>
          <label className="tpo-help-search-field" htmlFor="tpo-help-keyword">
            <span className="tpo-help-search-field__label">{fixedUi("关键词")}</span>
            <input
              id="tpo-help-keyword"
              className="tpo-input tpo-help-search-field__input"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              placeholder={fixedUi("搜索通知标题、内容或常见问题")}
            />
          </label>
          <button
            type="button"
            className="tpo-help-search-btn tpo-help-search-btn--ghost"
            onClick={handleResetSearch}
          >
            {fixedUi("重置")}
          </button>
          <button type="submit" className="tpo-help-search-btn tpo-help-search-btn--primary">
            {fixedUi("查询")}
          </button>
        </form>
      </header>

      <nav className="tpo-help-region tpo-help-region--nav" aria-label={fixedUi("帮助分区")}>
        <div className="tpo-help-page__tabs" role="tablist">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={`tpo-help-tab${tab === item.id ? ' is-active' : ''}`}
              onClick={() => switchTab(item.id)}
            >
              {fixedUi(item.label)}
            </button>
          ))}
        </div>
      </nav>

      {shopDimensionTabs}
      <section className="tpo-help-region tpo-help-region--content" aria-label={fixedUi("内容区")}>
        {tab === 'notices' ? (
          <>
            <div className="tpo-help-page__toolbar">
              <div className="tpo-help-page__filters" role="group" aria-label={fixedUi("通知渠道")}>
                {CHANNEL_FILTERS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`tpo-help-filter${channelFilter === opt.id ? ' is-active' : ''}`}
                    onClick={() => setChannelFilter(opt.id)}
                  >
                    {fixedUi(opt.label)}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="tpo-help-filter"
                onClick={loadAlerts}
                disabled={!activeShopId || loading}
              >
                {loading ? fixedUi("刷新中…") : fixedUi("刷新")}
              </button>
            </div>

            <div className="tpo-help-table-wrap">
              <table className="tpo-help-table">
                <thead>
                  <tr>
                    <th className="tpo-help-table__col--channel">{fixedUi("渠道")}</th>
                    <th className="tpo-help-table__col--level">{fixedUi("级别")}</th>
                    <th className="tpo-help-table__col--title">{fixedUi("标题")}</th>
                    <th className="tpo-help-table__col--msg">{fixedUi("内容")}</th>
                    <th className="tpo-help-table__col--source">{fixedUi("来源")}</th>
                    <th className="tpo-help-table__col--time">{fixedUi("时间")}</th>
                    <th className="tpo-help-table__col--ops">{fixedUi("操作")}</th>
                  </tr>
                </thead>
                <tbody>
                  {!activeShopId || loading || filteredAlerts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="tpo-help-table__empty">{emptyMessage}</td>
                    </tr>
                  ) : (
                    filteredAlerts.map((item) => {
                      const tone = resolveTemuAlertTone(item);
                      const isTodo = item.source === 'business';
                      return (
                        <tr key={item.id} className={`tpo-help-table__row--${tone}`}>
                          <td>{channelLabel(item.channel)}</td>
                          <td>
                            <span className={`tpo-help-level tpo-help-level--${tone}`}>
                              {levelLabel(tone)}
                            </span>
                          </td>
                          <td className="tpo-help-table__title">{item.title || '—'}</td>
                          <td className="tpo-help-table__msg">
                            <div>{item.message || '—'}</div>
                            {item.link ? (
                              <Link to={item.link} className="tpo-help-table__link">
                                {isTodo ? fixedUi("去处理") : fixedUi("查看")}
                              </Link>
                            ) : null}
                          </td>
                          <td>{sourceLabel(item.source)}</td>
                          <td>
                            <time dateTime={item.createdAt ? String(item.createdAt) : undefined}>
                              {formatTime(item.createdAt)}
                            </time>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="tpo-help-table__action"
                              disabled={busyId === item.id}
                              onClick={() => handleDismiss(item)}
                            >
                              {busyId === item.id ? '处理中…' : '完成(清除)'}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {tab === 'faq' ? (
          <div className="tpo-help-table-wrap">
            <table className="tpo-help-table">
              <thead>
                <tr>
                  <th className="tpo-help-table__col--faq-cat">{fixedUi("分类")}</th>
                  <th className="tpo-help-table__col--faq-title">{fixedUi("问题")}</th>
                  <th className="tpo-help-table__col--faq-answer">{fixedUi("说明")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredFaq.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="tpo-help-table__empty">{fixedUi("没有匹配的常见问题")}</td>
                  </tr>
                ) : (
                  filteredFaq.map((item) => (
                    <tr key={item.id}>
                      <td>{fixedUi(item.category)}</td>
                      <td className="tpo-help-table__title">{fixedUi(item.title)}</td>
                      <td className="tpo-help-table__msg">{fixedUi(item.answer)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}

        {tab === 'feedback' ? (
          <div className="tpo-help-feedback-panel">
            <p className="tpo-help-page__hint">
              提交后将直接发送到系统已配置的收件邮箱。下方联系邮箱用于我们回复进度；紧急授权/同步问题也可先查看「常见问题」。
            </p>
            <form className="tpo-help-feedback" onSubmit={submitFeedback}>
              <label className="tpo-field">
                <span className="tpo-field__label">{fixedUi("联系邮箱")}</span>
                <input
                  className="tpo-input"
                  type="email"
                  value={feedbackEmail}
                  onChange={(e) => setFeedbackEmail(e.target.value)}
                  placeholder={fixedUi("用于接收回复")}
                  disabled={feedbackSubmitting}
                  required
                />
              </label>
              <label className="tpo-field">
                <span className="tpo-field__label">{fixedUi("问题描述")}</span>
                <textarea
                  className="tpo-input tpo-help-feedback__textarea"
                  rows={10}
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={fixedUi("请说明页面路径、操作步骤、报错或异常现象")}
                  disabled={feedbackSubmitting}
                  required
                />
              </label>
              <div className="tpo-help-feedback__actions">
                <button
                  type="submit"
                  className="tpo-help-submit"
                  disabled={feedbackSubmitting}
                >
                  {feedbackSubmitting ? '发送中…' : '发送到系统邮箱'}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </section>
    </div>
  );
}
